export function QualifyingBuyers() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How to Qualify and Screen Potential Buyers
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Protect your investment by thoroughly vetting buyers and ensuring they have the financial capacity and experience to succeed
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Essential Buyer Qualification Criteria
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="bg-teal-50 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Financial Capacity</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Down Payment:</strong> Verify buyer has 20-30% available in liquid funds</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Cash Reserves:</strong> Confirm 6-12 months of payment reserves for emergencies</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Income Verification:</strong> Review tax returns, bank statements, and income sources</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Debt-to-Income:</strong> Ensure buyer's total debt obligations are manageable</span>
                  </li>
                </ul>
              </div>

              <div className="bg-teal-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-star-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Experience & Track Record</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Real Estate Experience:</strong> Prefer buyers with property management experience</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Portfolio Review:</strong> Examine buyer's current and past property investments</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Business Plan:</strong> Request detailed plan for property management and operations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>References:</strong> Contact previous sellers, lenders, and business partners</span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="bg-teal-50 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Credit & Background</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Credit Report:</strong> Review credit history and score (prefer 650+)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Payment History:</strong> Look for consistent on-time payment patterns</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Bankruptcies/Foreclosures:</strong> Understand any past financial difficulties</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Criminal Background:</strong> Conduct background check for serious issues</span>
                  </li>
                </ul>
              </div>

              <div className="bg-teal-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-file-list-3-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Documentation Required</h4>
                </div>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Personal Financial Statement:</strong> Complete asset and liability disclosure</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Tax Returns:</strong> Last 2-3 years of personal and business returns</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Bank Statements:</strong> Recent statements showing available funds</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                    <span><strong>Proof of Insurance:</strong> Evidence of ability to obtain property insurance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/19a0bb3726046b219654b6187dd39282.jpg"
              alt="Buyer qualification and screening process for seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Red Flags to Watch For
            </h3>
            <div className="space-y-4">
              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">Insufficient Down Payment:</strong>
                  <p className="text-gray-700">Buyer cannot provide at least 15-20% down payment from verified funds</p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">Pressure to Rush:</strong>
                  <p className="text-gray-700">Buyer pushes to close quickly without allowing proper due diligence</p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">Incomplete Documentation:</strong>
                  <p className="text-gray-700">Reluctance to provide financial statements, tax returns, or other verification</p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">No Real Estate Experience:</strong>
                  <p className="text-gray-700">First-time investor with no property management knowledge or support team</p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">Poor Credit Explanation:</strong>
                  <p className="text-gray-700">Cannot provide reasonable explanation for credit issues or payment problems</p>
                </div>
              </div>

              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <i className="ri-alert-line text-2xl text-red-600 mr-3 flex-shrink-0 mt-1"></i>
                <div>
                  <strong className="text-gray-900">Unrealistic Plans:</strong>
                  <p className="text-gray-700">Business plan shows lack of understanding of property operations or market conditions</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">The Ideal Buyer Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Strong Finances</h4>
              <p className="text-gray-300">25-30% down payment with substantial reserves</p>
            </div>

            <div>
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Proven Experience</h4>
              <p className="text-gray-300">Track record of successful property investments</p>
            </div>

            <div>
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Good Credit</h4>
              <p className="text-gray-300">Credit score 680+ with clean payment history</p>
            </div>

            <div>
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Complete Documentation</h4>
              <p className="text-gray-300">Transparent with all financial information</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}